var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo, useState } from "react";
import { ContentArea } from "./ContentArea";
import { ContentManagementProvider, defaultSnippetResolver } from "./ContentManagementProvider";
import { FaBoxesStacked, FaList, FaVideo } from "react-icons/fa6";
import { StackContentElement } from "./StackContentElement";
import { FaBox, FaCode, FaImage, FaLink, FaParagraph, FaQuestion, FaSquare, FaTextWidth } from "react-icons/fa";
import { ImageContentElement } from "./ImageContentElement";
import { TextContentElement } from "./TextContentElement";
import { ContainerContentElement } from "./ContainerContentElement";
import { StackItemContentElement } from "./StackItemContentElement";
import { HeroContentElement } from "./HeroContentElement";
import { TitleContentElement } from "./TitleContentElement";
import { BoxContentElement } from "./BoxContentElement";
import { GiFallingStar } from "react-icons/gi";
import { HighlightContentElement } from "./HighlightContentElement";
import { FormContentElement } from "./FormContentElement";
import { SnippetContentElement } from "./SnippetContentElement";
import { ButtonContentElement } from "./ButtonContentElement";
import { FAQContentElement } from "./FAQContentElement";
import { VideoContentElement } from "./VideoContentElement";
export const ContentEditor = (props) => {
    var _a;
    const [content, setContent] = useState([]);
    const [editable, setEditable] = useState(false);
    const [ref, setRef] = useState(null);
    const [ready, setReady] = useState(false);
    const [assetsData, setAssetsData] = useState([]);
    const editableNode = document.querySelector("#pimcore_editable_content");
    const staticNode = document.querySelector("#content_wrapper");
    const assetsNode = document.querySelector("#assets");
    const documentsNode = document.querySelector("#documents");
    const documentsData = useMemo(() => {
        if (documentsNode) {
            try {
                return JSON.parse(documentsNode.innerText);
            }
            catch (e) {
                console.error(e);
            }
        }
        return [];
    }, [ready]);
    useEffect(() => {
        if (assetsNode) {
            try {
                setAssetsData(JSON.parse(assetsNode.innerText));
            }
            catch (e) {
                console.error(e);
            }
        }
    }, [ready]);
    useEffect(() => {
        var _a;
        if (!ready) {
            return;
        }
        if (editableNode) {
            /**
             * Pimcore delivers the content elements as static HTML without content and then fills them
             * in runtime. This means that at this point of our initialization process, contents might not
             * yet be there, and we don't really have a reliable way of knowing when that is the case.
             * The only feasible solution is waiting a sensible amount of time.
             */
            setTimeout(() => {
                var _a;
                const json = (_a = editableNode.textContent) !== null && _a !== void 0 ? _a : editableNode.innerText;
                try {
                    const data = JSON.parse(json);
                    setEditable(true);
                    setContent(data);
                }
                catch (e) {
                    console.log("Editable node is defined but not parsable.");
                    console.error(e);
                    setEditable(true);
                    setContent([]);
                }
            }, 500);
            return;
        }
        if (staticNode) {
            const json = (_a = staticNode.textContent) !== null && _a !== void 0 ? _a : staticNode.innerText;
            try {
                const data = JSON.parse(json);
                setContent(data);
            }
            catch (e) {
                console.log("Static node is defined but not parsable.");
                console.log("Error encountered while trying to parse the following string");
                console.log(json);
                console.error(e);
                setContent([]);
            }
            return;
        }
        console.error("Neither static nor editable node defined. Defaulting to empty content");
    }, [ready]);
    useEffect(() => {
        if (editableNode) {
            editableNode.innerText = JSON.stringify(content);
        }
    }, [content]);
    useEffect(() => {
        const loader = () => {
            setTimeout(() => {
                if (ready) {
                    return;
                }
                if ((editableNode || staticNode) && assetsNode) {
                    setReady(true);
                }
            }, 15);
        };
        document.addEventListener("DOMContentLoaded", loader);
        loader();
        return () => {
            document.removeEventListener("DOMContentLoaded", loader);
        };
    }, []);
    return (React.createElement(ContentManagementProvider, { settings: {
            contentElementDefinitions: [
                {
                    type: "stack",
                    label: "Stack",
                    icon: React.createElement(FaBoxesStacked, null),
                    render: StackContentElement
                },
                {
                    type: "image",
                    label: "Image",
                    icon: React.createElement(FaImage, null),
                    render: ImageContentElement
                },
                {
                    type: "text",
                    label: "Text",
                    icon: React.createElement(FaParagraph, null),
                    render: TextContentElement
                },
                {
                    type: "container",
                    label: "Container",
                    icon: React.createElement(FaSquare, null),
                    render: ContainerContentElement
                },
                {
                    type: "stackItem",
                    label: "Stack Item",
                    icon: React.createElement(FaBox, null),
                    render: StackItemContentElement
                },
                {
                    type: "hero",
                    label: "Hero",
                    icon: React.createElement(FaImage, null),
                    render: HeroContentElement
                },
                {
                    type: "title",
                    label: "Title",
                    icon: React.createElement(FaTextWidth, null),
                    render: TitleContentElement
                },
                {
                    type: "box",
                    label: "Box",
                    icon: React.createElement(FaBox, null),
                    render: BoxContentElement
                },
                {
                    type: "highlight",
                    label: "Highlight",
                    icon: React.createElement(GiFallingStar, null),
                    render: HighlightContentElement
                },
                {
                    type: "form",
                    label: "Form",
                    icon: React.createElement(FaList, null),
                    render: FormContentElement
                },
                {
                    type: "snippet",
                    label: "Snippet",
                    icon: React.createElement(FaCode, null),
                    render: SnippetContentElement
                },
                {
                    type: "button",
                    label: "Button",
                    icon: React.createElement(FaLink, null),
                    render: ButtonContentElement
                },
                {
                    type: "faq",
                    label: "Question Group",
                    icon: React.createElement(FaQuestion, null),
                    render: FAQContentElement
                },
                {
                    type: "video",
                    label: "Video",
                    icon: React.createElement(FaVideo, null),
                    render: VideoContentElement
                }
            ],
            isEditing: editable,
            resolvers: {
                document: editable ? ((id) => __awaiter(void 0, void 0, void 0, function* () { return id.toString(); })) : ((id) => __awaiter(void 0, void 0, void 0, function* () {
                    var _b, _c;
                    if (!documentsNode) {
                        return id.toString();
                    }
                    return (_c = (_b = documentsData.find(item => item.id === id || item.path === id)) === null || _b === void 0 ? void 0 : _b.url) !== null && _c !== void 0 ? _c : id.toString();
                })),
                snippet: defaultSnippetResolver,
                asset: (id) => __awaiter(void 0, void 0, void 0, function* () {
                    if (!assetsNode) {
                        return null;
                    }
                    const found = assetsData.find(item => item.id === id);
                    if (!found) {
                        try {
                            const data = yield (yield fetch("/api/asset/" + id)).json();
                            setAssetsData([...assetsData, data]);
                            return data;
                        }
                        catch (e) {
                            return null;
                        }
                    }
                    return found;
                })
            }
        } },
        React.createElement("div", { ref: setRef, style: { paddingBottom: "50px" } },
            React.createElement(ContentArea, { attachControlsTo: ref, showAdd: true, contentElements: content, onChange: setContent, allowedTypes: (_a = props.allowedTypes) !== null && _a !== void 0 ? _a : ["container", "hero", "highlight", "snippet"] }))));
};
